import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retryCount: number;
}

export class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  private debugMode = false;

  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
    
    // Ativar modo debug se estiver em desenvolvimento ou com query param
    this.debugMode = process.env.NODE_ENV === 'development' || 
                     window.location.search.includes('debug=true');
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.error('ErrorBoundary caught error:', error);
    
    // Filter out authentication-related errors - let them be handled locally
    const isAuthError = (
      error.message?.toLowerCase().includes('invalid login credentials') ||
      error.message?.toLowerCase().includes('credentials') ||
      error.message?.toLowerCase().includes('authentication') ||
      error.message?.toLowerCase().includes('login') ||
      error.message?.toLowerCase().includes('auth') ||
      error.message?.toLowerCase().includes('signin') ||
      error.message?.toLowerCase().includes('signup')
    );
    
    if (isAuthError) {
      console.log('🔒 [ErrorBoundary] Ignorando erro de autenticação:', error.message);
      // Don't catch auth errors - let them be handled by auth components
      return { hasError: false, retryCount: 0 };
    }
    
    // Auto-retry for common DOM/chunk errors
    const isRetriableError = (
      error.message?.includes('insertBefore') ||
      error.message?.includes('NotFoundError') ||
      error.message?.includes('ChunkLoadError') ||
      error.message?.includes('Loading chunk') ||
      error.name === 'ChunkLoadError'
    );
    
    if (isRetriableError) {
      // Wait a bit and reload the page for chunk errors
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
    
    return { hasError: true, error, retryCount: 0 };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Logging estruturado com mais detalhes
    const errorDetails = {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      errorInfo: info,
      location: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      retryCount: this.state.retryCount,
    };

    console.error('[ErrorBoundary] Erro capturado:', errorDetails);
    
    // Salvar erro no localStorage para debug
    try {
      const existingErrors = JSON.parse(localStorage.getItem('errorBoundary_logs') || '[]');
      existingErrors.push(errorDetails);
      // Manter apenas os últimos 10 erros
      if (existingErrors.length > 10) existingErrors.shift();
      localStorage.setItem('errorBoundary_logs', JSON.stringify(existingErrors));
    } catch (e) {
      console.warn('Não foi possível salvar erro no localStorage:', e);
    }

    this.setState({ errorInfo: info });
  }

  handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;
    console.log(`[ErrorBoundary] Tentativa de retry ${newRetryCount}`);
    
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      retryCount: newRetryCount 
    });
    
    try {
      // Limpar possíveis states corruptos
      sessionStorage.removeItem('auth_state');
      
      // Soft reload sem recarregar a página completamente
      if (typeof window !== 'undefined') {
        window.history?.replaceState?.(null, '', window.location.href);
      }
    } catch (e) {
      console.warn('Erro no soft reload:', e);
    }
  };

  handleHardReload = () => {
    console.log('[ErrorBoundary] Executando hard reload');
    window.location.reload();
  };

  clearErrorLogs = () => {
    localStorage.removeItem('errorBoundary_logs');
    console.log('[ErrorBoundary] Logs de erro limpos');
  };

  render() {
    if (this.state.hasError) {
      const isTemporaryError = this.state.error?.message?.includes('insertBefore') ||
                              this.state.error?.message?.includes('NotFoundError') ||
                              this.state.error?.name === 'ChunkLoadError';

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-lg w-full text-center space-y-6">
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-destructive">
                {isTemporaryError ? 'Erro Temporário' : 'Ocorreu um erro inesperado'}
              </h1>
              <p className="text-muted-foreground">
                {isTemporaryError 
                  ? 'Um erro temporário foi detectado. Tente novamente em alguns segundos.'
                  : 'Algo deu errado. Tente uma das opções abaixo.'}
              </p>
            </div>

            {this.debugMode && this.state.error && (
              <div className="bg-muted p-4 rounded-md text-left text-sm">
                <p className="font-medium text-destructive mb-2">Detalhes do Erro (Debug):</p>
                <p><strong>Tipo:</strong> {this.state.error.name}</p>
                <p><strong>Mensagem:</strong> {this.state.error.message}</p>
                <p><strong>Tentativas:</strong> {this.state.retryCount}</p>
                {this.state.error.stack && (
                  <details className="mt-2">
                    <summary className="cursor-pointer">Stack Trace</summary>
                    <pre className="mt-2 text-xs whitespace-pre-wrap overflow-hidden">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-center gap-3">
                <button 
                  onClick={this.handleRetry} 
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={this.state.retryCount >= 3}
                >
                  {this.state.retryCount >= 3 ? 'Limite de tentativas' : `Tentar novamente ${this.state.retryCount > 0 ? `(${this.state.retryCount}/3)` : ''}`}
                </button>
                <button 
                  onClick={this.handleHardReload} 
                  className="px-4 py-2 rounded-md border border-border hover:bg-accent"
                >
                  Recarregar página
                </button>
              </div>
              
              {this.debugMode && (
                <button 
                  onClick={this.clearErrorLogs}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Limpar logs de erro
                </button>
              )}
            </div>

            <div className="text-xs text-muted-foreground">
              Se o erro persistir, tente:
              <ul className="mt-1 space-y-1">
                <li>• Limpar cache do navegador</li>
                <li>• Abrir em uma aba anônima</li>
                <li>• Verificar sua conexão com a internet</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
